import type { GraphData, ValidatedFormFields } from './types';

const COMPOUNDED_MONTHLY = 12;

function monthlyRateFromApyDecimal(apyAsDecimal: number): number {
  if (apyAsDecimal <= 0) {
    return 0;
  }
  return (1 + apyAsDecimal) ** (1 / COMPOUNDED_MONTHLY) - 1;
}

/**
 * Decimal places when rounding the implied **nominal annual rate (APR-style)** before dividing by 12.
 * Rounding to 4 decimal places (e.g. 0.0325 for "3.25%") matches many bank APY calculators that derive a
 * monthly periodic rate as `(round(12 * ((1 + APY)^(1/12) − 1), 4)) / 12` instead of using the exact
 * monthly factor `(1 + APY)^(1/12) − 1` directly.
 */
const APY_TO_NOMINAL_APR_ROUND_DECIMAL_PLACES = 4;

/**
 * Monthly periodic rate used by the HYSA compound projection: implied nominal APR from APY (for monthly
 * compounding), rounded, then divided by 12.
 */
export function apyToMonthlyRateFromRoundedNominalApr(apyAsDecimal: number): number {
  if (apyAsDecimal <= 0) {
    return 0;
  }
  const nominalAprDecimal = 12 * monthlyRateFromApyDecimal(apyAsDecimal);
  const factor = 10 ** APY_TO_NOMINAL_APR_ROUND_DECIMAL_PLACES;
  const nominalAprRounded = Math.round(nominalAprDecimal * factor) / factor;
  return nominalAprRounded / COMPOUNDED_MONTHLY;
}

export function isAmbiguousAnnualApyString(value: string): boolean {
  return value.endsWith('.') || value === '.';
}

type RatePerPaymentPeriodInput = {
  annualInterestRate: number;
  compoundPeriodsPerYear: number;
  numberOfPaymentsPerYear: number;
};

/**
 * Calculate the effective interest rate per payment period
 * Formula: rate = ((1+r/n)^(n/p))-1
 * https://www.vertex42.com/Calculators/compound-interest-calculator.html#rate-per-period
 * @param annualInterestRate - Annual interest rate as a decimal (e.g., 0.05 for 5%)
 * @param compoundPeriodsPerYear - Number of times interest compounds per year
 * @param numberOfPaymentsPerYear - Number of payments made per year
 * @returns The effective rate per payment period
 */
export function calculateRatePerPaymentPeriod({
  annualInterestRate,
  compoundPeriodsPerYear,
  numberOfPaymentsPerYear,
}: RatePerPaymentPeriodInput) {
  const ratio = annualInterestRate / compoundPeriodsPerYear;
  return (1 + ratio) ** (compoundPeriodsPerYear / numberOfPaymentsPerYear) - 1;
}

type FutureValueOfPrincipalInputs = {
  principal: number;
  ratePerPeriod: number;
  numberOfPeriods: number;
};

/**
 * Calculate future value of a principal amount
 * Formula: FV = P*(1+rate)^nper
 * https://www.vertex42.com/Calculators/compound-interest-calculator.html#formula
 * @param principal - Initial principal amount
 * @param ratePerPeriod - Interest rate per period as decimal
 * @param numberOfPeriods - Total number of periods
 * @returns Future value of the principal
 */
export function calculateFutureValueOfPrincipal({
  principal,
  ratePerPeriod,
  numberOfPeriods,
}: FutureValueOfPrincipalInputs) {
  return principal * (1 + ratePerPeriod) ** numberOfPeriods;
}

/**
 * Calculate compound interest for both principal and series of payments
 *
 * Let `APY` be the annual percentage yield as a decimal (e.g. `annualPercentageYield / 100`).
 *
 * `rate = round(12 * ((1 + APY)^(1/12) - 1), 4) / 12`
 *
 * Let `P` = `initialDeposit`, `A` = `monthlyDeposit`, `nper` = `12 * timePeriod` months.
 * Same as looping each month with “interest then deposit”: when `rate !== 0`, ending balance is
 *
 * `F = P*(1+rate)^nper + A*(((1+rate)^nper - 1) / rate)`
 *
 * When `rate === 0`: `F = P + A*nper`.
 * @param initialDeposit - Initial principal amount
 * @param monthlyDeposit - Monthly payment amount
 * @param annualPercentageYield - Annual percentage yield (e.g., 5 for 5%)
 * @param timePeriod - Time period in years
 * @returns Object containing interestEarned, total, totalInvested, and totalMonthlyContributions
 */
export function calculateCompoundInterest({
  initialDeposit,
  monthlyDeposit,
  annualPercentageYield,
  timePeriod,
}: ValidatedFormFields) {
  const rateAsDecimal = annualPercentageYield / 100;
  const monthlyRate = apyToMonthlyRateFromRoundedNominalApr(rateAsDecimal);
  const totalMonths = timePeriod * COMPOUNDED_MONTHLY;
  let balance = initialDeposit;
  let totalMonthlyContributions = 0;

  const data: GraphData[] = [];
  const numberOfBars = timePeriod === 1 || timePeriod === 3 ? 3 : 5;

  const barIndex = Math.round(totalMonths / numberOfBars);

  for (let month = 1; month <= totalMonths; month++) {
    balance *= 1 + monthlyRate;
    balance += monthlyDeposit;
    totalMonthlyContributions += monthlyDeposit;
    const interestEarned = balance - initialDeposit - totalMonthlyContributions;

    if (month % barIndex !== 0) {
      continue;
    }

    const name = timePeriod === 1 ? month : Math.round(month / COMPOUNDED_MONTHLY);

    data.push({
      name: name.toString(),
      // these values must match BAR_GRAPH_ORDER
      interestEarned,
      initialDeposit,
      monthlyDeposits: totalMonthlyContributions,
    });
  }

  const total = balance;
  const totalInvested = initialDeposit + totalMonthlyContributions;
  const interestEarned = total - totalInvested;

  return {
    graphData: data,
    initialDeposit,
    interestEarned,
    total,
    totalInvested,
    totalMonthlyContributions,
  };
}

export function formatToDisplayValue(value: string) {
  if (value === '') {
    return value;
  }

  let suffix = '';
  if (value.endsWith('.')) {
    suffix = '.';
  } else if (value.endsWith('.0')) {
    suffix = '.0';
  }

  return `${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}${suffix}`;
}

export function transformValuesToString({
  initialDeposit,
  monthlyDeposit,
  annualPercentageYield,
  timePeriod,
}: ValidatedFormFields) {
  return {
    initialDeposit: initialDeposit.toLocaleString('en-US'),
    monthlyDeposit: monthlyDeposit.toLocaleString('en-US'),
    annualPercentageYield: annualPercentageYield.toLocaleString('en-US'),
    timePeriod: timePeriod.toLocaleString('en-US'),
  };
}

type FormatCurrencyInputs = {
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};
export function formatCurrency({
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
}: FormatCurrencyInputs) {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  });
}
